// src/utils/selectorsJob.js
import { jtbdKeyFromName, normProductName, toNumber, clamp } from './normalize';
import { computeWinScore, computeGapScore } from './scoring';

// small utils
const take = (arr, n) => (Array.isArray(arr) ? arr.slice(0, n) : []);
const avg  = (xs) => (xs.length ? xs.reduce((a,b)=>a+b,0)/xs.length : 0);
const capSentence = (s) => {
  const t = (s || '').toString().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
};
const get = (obj, path, dflt) => {
  try {
    const parts = Array.isArray(path) ? path : String(path).split('.');
    let cur = obj;
    for (const p of parts) { if (cur == null) return dflt; cur = cur[p]; }
    return cur == null ? dflt : cur;
  } catch { return dflt; }
};

// --- receipts util (quote + optional timestamp for video icon) ---
function extractReceipts(node, limit = 2) {
  if (!node) return [];
  const push = (arr, quote, url, ts) => {
    const q = (quote || '').toString().trim();
    if (!q) return;
    arr.push({ quote: q, url: url || '', timestamp: (Array.isArray(ts) ? ts[0] : ts) || '' });
  };
  const candidates = [];
  const collect = (val) => Array.isArray(val)
    ? val.filter(Boolean)
    : (val && typeof val === 'object' ? Object.values(val).filter(Boolean) : []);
  const generics = []
    .concat(collect(get(node,'receipts')))
    .concat(collect(get(node,'quotes')))
    .concat(collect(get(node,'representative_quotes')))
    .concat(collect(get(node,'evidence')));
  for (const r of generics) {
    if (typeof r === 'string') { push(candidates, r, '', r.timestamp || r.ts); }
    else if (r && typeof r === 'object') {
      push(candidates, r.quote || r.text || r.snippet || r.value, r.url || r.link || '', r.timestamp || r.ts || r.timecode);
    }
  }
  if (node && typeof node === 'object') {
    for (const k of Object.keys(node)) {
      if (/_quote$/.test(k) && node[k]) {
        const base = k.replace(/_quote$/,'');
        const ts = node[`${base}_timestamp`] ?? node[`${base}_timestamps`];
        push(candidates, node[k], '', ts);
      }
    }
    if (node.quote || node.timestamp) push(candidates, node.quote, '', node.timestamp);
  }
  const out = []; const seen = new Set();
  for (const rec of candidates) {
    const key = (rec.quote || '').trim().toLowerCase();
    if (key && !seen.has(key)) { seen.add(key); out.push(rec); }
    if (out.length >= limit) break;
  }
  return out;
}

/* ------------------------------ core builders ------------------------------ */

// Select all (story, job) pairs for this Job
function selectPairsForJob(stories, jobName) {
  const key = jtbdKeyFromName(jobName);
  const results = [];
  for (const s of (stories || [])) {
    for (const j of (s.jobs_to_be_done || [])) {
      const name = j.job_to_be_done_category || j.job_to_be_done || j.raw_job_to_be_done || '';
      if (!name) continue;
      if (jtbdKeyFromName(name) === key) results.push({ story: s, job: j });
    }
  }
  return results;
}

// ------------ TOP OF PAGE: RELATED JOB CHIPS ------------
function buildRelatedJobsChips(pairs) {
  const counts = new Map();
  const total = pairs.length || 0;

  for (const { story, job } of pairs) {
    const selfKey = jtbdKeyFromName(job.job_to_be_done_category || job.job_to_be_done || '');
    const others = new Set(
      (story.jobs_to_be_done || [])
        .map(j => j.job_to_be_done_category || j.job_to_be_done || '')
        .filter(Boolean)
        .map(jtbdKeyFromName)
    );
    others.delete(selfKey);
    for (const k of others) counts.set(k, (counts.get(k) || 0) + 1);
  }

  return Array.from(counts.entries())
    .sort((a,b)=>b[1]-a[1])
    .map(([k,count]) => ({
      title: capSentence(k),
      count,
      percent: total ? Math.round((count/total)*100) : 0
    }));
}

// ------------ SUMMARY OF JOB (Overview) ------------
function buildSummaryOfJob(pairs, jtbdSummary /* kept only for drivers/text */) {
  const impVals = [];
  const satVals = [];
  const quotes = [];

  for (const { job, story } of pairs) {
    const imp = toNumber(job.importance ?? job.importance_score);
    const sat = toNumber(job.satisfaction ?? job.satisfaction_score ?? job.satisfaction_level);
    if (Number.isFinite(imp)) impVals.push(imp);
    if (Number.isFinite(sat)) satVals.push(sat);

    // quick receipts
    const jr = extractReceipts(job, 1), sr = extractReceipts(story, 1);
    if (jr.length) quotes.push(jr[0]);
    if (sr.length) quotes.push(sr[0]);
  }

  const avgImportance   = Number((satVals.length ? (impVals.reduce((a,b)=>a+b,0)/impVals.length) : 0).toFixed(1));
  const avgSatisfaction = Number((satVals.length ? (satVals.reduce((a,b)=>a+b,0)/satVals.length) : 0).toFixed(1));
  const gap = Number((avgImportance - avgSatisfaction).toFixed(1));

  const summaryText        = jtbdSummary?.summary_text || jtbdSummary?.summary || '';
  const topPositiveDrivers = jtbdSummary?.top_positive_drivers || [];
  const topNegativeDrivers = jtbdSummary?.top_negative_drivers || [];
  const representativeQuotes = take(quotes, 2);

  // 11 buckets (0..10). Round to nearest int, clamp 0..10
  const histogram = Array.from({ length: 11 }, () => 0);
  for (const vRaw of satVals) {
    let v = Math.round(vRaw);
    if (v < 0) v = 0;
    if (v > 10) v = 10;
    histogram[v] += 1;
  }

  return {
    type: 'jobSatisfactionSummary',
    title: 'Summary of Job',
    avgImportance,
    avgSatisfaction,
    gap,
    histogram,                // now 11 bins for 0..10
    interviewCount: pairs.length,
    summaryText,
    topPositiveDrivers,
    topNegativeDrivers,
    representativeQuotes
  };
}


// ------------ MEASURES (top 4) ------------
function buildMeasures(pairs) {
  const buckets = new Map();
  for (const { job } of pairs) {
    const measures =
      job.how_they_measure_satisfaction ||
      job.measures ||
      job.how_do_they_measure_success ||
      [];
    for (const m of measures) {
      const title = m.measure_of_satisfaction_category || m.title || m.name || '';
      if (!title) continue;
      const k = title.toLowerCase();
      const b = buckets.get(k) || { title, count: 0, objs: [] };
      b.count += 1;
      b.objs.push(m);
      buckets.set(k, b);
    }
  }

  return Array.from(buckets.values())
    .sort((a,b)=>b.count-a.count)
    .slice(0,4)
    .map(b => {
      // choose 2 random quotes from the matching objects’ `quote` field
      const quotes = b.objs
        .map(o => ({
          quote: (o.quote || '').toString().trim(),
          url: '',
          timestamp: o.timestamp || o.ts || ''
        }))
        .filter(q => q.quote);
      const receipts = take(quotes.sort(()=>Math.random()-0.5), 2);
      return { type: 'measure', title: capSentence(b.title), count: b.count, receipts };
    });
}

// ------------ JOB MAP (preserve order; add stageIndex for numbering) ------------

function normalizeStages(mapObj) {
  if (!mapObj) return [];
  const raw = mapObj.stages;
  if (Array.isArray(raw)) return raw.map(s => ({ stage: s.stage || s.name || s.title || '', steps: Array.isArray(s.steps) ? s.steps : [] }));
  if (raw && typeof raw === 'object') {
    const out = [];
    for (const [stageKey, val] of Object.entries(raw)) {
      const steps = Array.isArray(val) ? val : (val?.steps || []);
      out.push({ stage: stageKey, steps });
    }
    return out;
  }
  return [];
}

// ---- Robust Job Map finder (key or metadata) ----
function findJobMapForTitle(jobMaps, jtbdTitle) {
  if (!jobMaps) return undefined;
  const want = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g,' ').replace(/[^\w\s]/g,'');

  const target = want(jtbdTitle);
  const keys = Object.keys(jobMaps || {});

  // 1) direct key match/contains
  for (const k of keys) {
    const nk = want(k);
    if (nk === target || nk.includes(target) || target.includes(nk)) return jobMaps[k];
  }

  // 2) inspect metadata in each map
  for (const k of keys) {
    const m = jobMaps[k];
    const titleFields = [
      m?.title,
      m?.job_title,
      m?.job_to_be_done,
      m?.job_to_be_done_category,
      m?.name,
    ].filter(Boolean);
    for (const tf of titleFields) {
      const nt = want(tf);
      if (nt === target || nt.includes(target) || target.includes(nt)) return m;
    }
  }
  return undefined;
}

// REPLACE your existing buildJobMap with this resilient version:
// --- REPLACE ONLY THIS FUNCTION ---

function buildJobMap(jobMaps, jobName) {
  // Dev log helper
  const devWarn = (msg, extra) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[buildJobMap]', msg, extra || '');
    }
  };

  if (!jobMaps || typeof jobMaps !== 'object') {
    devWarn('jobMaps missing/invalid');
    return { type: 'jobMapBoard', columns: [] };
  }

  const norm = (s) =>
    (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ').replace(/[^\w\s]/g, '');
  const slug = jtbdKeyFromName(jobName);
  const want = norm(jobName);

  // 1) Collect best candidates in a forgiving order
  const candidates = [];
  // direct by slug
  if (jobMaps[slug]) candidates.push({ key: slug, map: jobMaps[slug] });
  // direct by normalized label (some stores use readable keys)
  if (jobMaps[want]) candidates.push({ key: want, map: jobMaps[want] });
  // scan keys for fuzzy match
  for (const [k, m] of Object.entries(jobMaps)) {
    const nk = norm(k);
    if (nk === want || nk.includes(want) || want.includes(nk)) {
      candidates.push({ key: k, map: m });
    }
  }
  // metadata scan (title fields)
  if (!candidates.length) {
    for (const [k, m] of Object.entries(jobMaps)) {
      const fields = [m?.title, m?.job_title, m?.job_to_be_done, m?.job_to_be_done_category, m?.name].filter(Boolean);
      if (fields.some(tf => {
        const nt = norm(tf);
        return nt === want || nt.includes(want) || want.includes(nt);
      })) {
        candidates.push({ key: k, map: m });
      }
    }
  }

  // fail fast if none
  if (!candidates.length) {
    devWarn('No candidate found for job map', { jobName, keys: Object.keys(jobMaps) });
    return { type: 'jobMapBoard', columns: [] };
  }

  // 2) Resolve stages from the first viable candidate, probing common wrappers
  const pickStages = (m) => {
    // common shapes we’ve seen:
    // - m.stages (array or dict)
    // - m.job_map.stages
    // - m.columns (legacy)
    // - wrapped array: [ { stages: [...] } ]
    let src =
      m?.stages ??
      m?.job_map?.stages ??
      (Array.isArray(m?.columns) ? m.columns.map(c => ({ stage: c.stage, steps: c.steps })) : undefined);

    // If `m` itself is an array, try first element
    if (!src && Array.isArray(m) && m[0]) {
      src = m[0].stages || m[0].job_map?.stages;
    }

    // Normalize dict → array
    if (src && !Array.isArray(src) && typeof src === 'object') {
      const out = [];
      for (const [stageKey, val] of Object.entries(src)) {
        const steps = Array.isArray(val) ? val : (val?.steps || []);
        out.push({ stage: stageKey, steps });
      }
      return out;
    }

    // Already an array
    if (Array.isArray(src)) {
      return src.map(s => ({
        stage: s.stage || s.name || s.title || '',
        steps: Array.isArray(s.steps) ? s.steps : (Array.isArray(s) ? s : [])
      }));
    }

    return [];
  };

  let mapObj = null;
  let stages = [];
  for (const c of candidates) {
    const st = pickStages(c.map);
    if (Array.isArray(st) && st.length) {
      mapObj = c.map;
      stages = st;
      break;
    }
  }

  if (!stages.length) {
    devWarn('Candidate(s) found but no stages extracted', {
      jobName,
      tried: candidates.map(c => c.key)
    });
    return { type: 'jobMapBoard', columns: [] };
  }

  // 3) Apply exact stage order
  const ORDER = ['define', 'locate', 'prepare', 'confirm', 'execute', 'monitor', 'modify', 'conclude'];
  const idxOf = (stageName) => {
    const i = ORDER.indexOf(String(stageName || '').toLowerCase());
    return i === -1 ? 999 : i;
  };
  const sorted = [...stages].sort(
    (a, b) => idxOf(a.stage || a.name || a.title) - idxOf(b.stage || b.name || b.title)
  );

  // 4) Map to columns; include modal data + "How they do it:" text (kept in description)
  const columns = sorted.map((stage, si) => {
    const base = stage.stage || stage.name || stage.title || '';
    const label = `${si + 1}. ${base}`;
    const steps = (stage.steps || []).map((step) => ({
      name: step.step_name || step.name || step.title || '',
      description: step.description || '',
      satisfaction_score: toNumber(step.satisfaction_score),
      satisfaction_score_justification: step.satisfaction_score_justification || '',
      // These are shown inline on the card
      receipts: extractReceipts(step, 2),
      // And the full array for the modal:
      product_workflow_steps: Array.isArray(step.product_workflow_steps) ? step.product_workflow_steps : []
    }));
    return { stage: label, steps };
  });

  if (!columns.length) {
    devWarn('Columns ended empty after mapping', { jobName });
  }

  return { type: 'jobMapBoard', columns };
}




// ------------ TRIGGERS (top 4) — from STORY-LEVEL fields ------------
function buildTriggersForJob(pairs) {
  // Aggregate by trigger_category at the STORY level,
  // but only consider stories that include the selected job (pairs scope).
  const seenStories = new Set();
  const buckets = new Map(); // key -> { title, count, quotes[] }

  const addBucket = (title) => {
    if (!title) return null;
    const k = title.toLowerCase();
    const b = buckets.get(k) || { title, count: 0, quotes: [] };
    buckets.set(k, b);
    return b;
  };

  const addQuote = (bucket, quote) => {
    if (!bucket || !quote) return;
    if (bucket.quotes.length < 8) { // keep a small pool; we’ll slice to 2 later
      bucket.quotes.push({ quote: String(quote), url: '', timestamp: '' });
    }
  };

  for (const { story } of pairs) {
    if (seenStories.has(story)) continue;
    seenStories.add(story);

    // Case 1: simple top-level fields
    if (story.trigger_category) {
      const b = addBucket(story.trigger_category);
      if (b) b.count += 1;
      if (b && story.trigger) addQuote(b, story.trigger);
    }

    // Case 2: arrays of triggers at the top level
    const arrays = [story.triggers, story.switching_triggers, story.why_customers_switch].filter(Boolean);
    for (const arr of arrays) {
      for (const t of arr) {
        const title = t?.trigger_category || t?.title || t?.reason || t?.text || '';
        if (!title) continue;
        const b = addBucket(title);
        if (b) b.count += 1;
        // The quote should be the 'trigger' value in the interview item
        const q = t?.trigger || t?.quote || '';
        if (q) addQuote(b, q);
      }
    }
  }

  return Array.from(buckets.values())
    .sort((a,b) => b.count - a.count)
    .slice(0, 4)
    .map(b => ({
      type: 'trigger',
      title: b.title,
      count: b.count,
      receipts: (b.quotes || []).slice(0, 2) // up to 2 quotes per tile
    }));
}


function buildTopSituationsForJob(pairs, sectionTotal) {
  // For each pair.job (already scoped to the selected job),
  // read the single field `situation_category` and count it.
  const buckets = new Map();
  for (const { job } of pairs) {
    const title =
      job.situation_category ||
      job.situation ||
      job.context ||
      ''; // aliases, in case files use a slightly different key
    if (!title) continue;
    const key = String(title).toLowerCase();
    const b = buckets.get(key) || { title, count: 0 };
    b.count += 1;
    buckets.set(key, b);
  }

  return Array.from(buckets.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 4)
    .map((b) => ({
      type: 'measure',           // simple count tile fits well visually
      title: b.title,
      count: b.count,
      // percent shown by Tile using sectionTotal if desired; no receipts requested
    }));
}


function buildTopDesiredOutcomesForJob(pairs, sectionTotal) {
  const buckets = new Map();
  for (const { job } of pairs) {
    const title =
      job.desired_outcome_category ||
      job.desired_outcome ||
      job.outcome ||
      '';
    if (!title) continue;
    const key = String(title).toLowerCase();
    const b = buckets.get(key) || { title, count: 0 };
    b.count += 1;
    buckets.set(key, b);
  }

  return Array.from(buckets.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 4)
    .map((b) => ({
      type: 'measure',
      title: b.title,
      count: b.count,
    }));
}



// ------------ TOP SITUATIONS (chips) — from selected job objects ------------
function buildTopSituationsChips(pairs) {
  const cnt = new Map();
  const total = pairs.length || 0;

  for (const { job } of pairs) {
    const items =
      job.situations ||
      job.situations_when_doing_this_job ||
      job.top_situations ||
      [];
    for (const it of items) {
      const title = it.situation_category || it.title || it.situation || '';
      if (!title) continue;
      const k = title.toLowerCase();
      const cur = cnt.get(k) || { title, count: 0 };
      cur.count += 1;
      cnt.set(k, cur);
    }
  }

  return Array.from(cnt.values())
    .sort((a,b)=>b.count-a.count)
    .slice(0,4)
    .map(x => ({
      title: x.title,
      count: x.count,
      percent: total ? Math.round((x.count/total)*100) : 0
    }));
}


// ------------ TOP DESIRED OUTCOMES (chips) — from selected job objects ------------
function buildTopDesiredOutcomesChips(pairs) {
  const cnt = new Map();
  const total = pairs.length || 0;

  for (const { job } of pairs) {
    const items =
      job.desired_outcomes ||
      job.top_desired_outcomes ||
      job.outcomes ||
      [];
    for (const it of items) {
      const title = it.desired_outcome_category || it.title || it.outcome || '';
      if (!title) continue;
      const k = title.toLowerCase();
      const cur = cnt.get(k) || { title, count: 0 };
      cur.count += 1;
      cnt.set(k, cur);
    }
  }

  return Array.from(cnt.values())
    .sort((a,b)=>b.count-a.count)
    .slice(0,4)
    .map(x => ({
      title: x.title,
      count: x.count,
      percent: total ? Math.round((x.count/total)*100) : 0
    }));
}


// ------------ PRODUCTS used for this Job (Importance × Satisfaction) ------------
function buildProductsUsedForJob(pairs) {
  const buckets = new Map();

  for (const { job, story } of pairs) {
    const prod = normProductName(story.solution_choice_category || story.solution_choice || '');
    if (!prod) continue;

    const imp = toNumber(job.importance ?? job.importance_score);
    const sat = toNumber(job.satisfaction ?? job.satisfaction_score ?? job.satisfaction_level);

    const b = buckets.get(prod) || {
      title: prod, importanceVals: [], satisfactionVals: [], count: 0,
      receiptsAll: [] // we'll later split into good/bad
    };
    if (Number.isFinite(imp)) b.importanceVals.push(imp);
    if (Number.isFinite(sat)) b.satisfactionVals.push(sat);
    b.count += 1;

    // collect satisfaction receipt candidates from the job object
    // (spec: every satisfaction_quote has satisfaction_timestamps)
    const q = job.satisfaction_quote || job.quote;
    const ts = job.satisfaction_timestamps || job.satisfaction_timestamp || job.timestamp || '';
    if (q) {
      const ts1 = Array.isArray(ts) ? ts[0] : ts;
      b.receiptsAll.push({ satisfaction: Number.isFinite(sat) ? sat : null, quote: String(q), timestamp: ts1 || '' });
    }

    buckets.set(prod, b);
  }

  // Build tiles per product
  const tiles = Array.from(buckets.values()).map((b) => {
    const importance   = Number(avg(b.importanceVals).toFixed(1));
    const satisfaction = Number(avg(b.satisfactionVals).toFixed(1));
    const winScore     = computeWinScore({ importance, satisfaction, interviews: b.count });
    const gapScore     = computeGapScore({ importance, satisfaction });
    const variant      = (gapScore > 0 && satisfaction <= importance * 0.9) ? 'gap' : 'win';

    // Split receipts into good (highest satisfaction) and bad (lowest)
    const withSat = b.receiptsAll.filter(r => r.satisfaction != null && r.quote);
    const good = [...withSat].sort((a,b)=>b.satisfaction - a.satisfaction).slice(0,2)
      .map(r => ({ quote: r.quote, url: '', timestamp: r.timestamp }));
    const bad  = [...withSat].sort((a,b)=>a.satisfaction - b.satisfaction).slice(0,2)
      .map(r => ({ quote: r.quote, url: '', timestamp: r.timestamp }));

    return {
      type: 'productForJob',
      variant,
      title: b.title,
      importance,
      satisfaction,
      count: b.count,
      receiptsGood: good,
      receiptsBad: bad
    };
  });

  return tiles;
}

// ------------ INSIGHTS (unchanged from your last working rev) ------------
function buildGoodBadInsightsForJob(pairs) {
  const pos = new Map();
  const neg = new Map();

  const feed = (bucket, title, intensity, receipts) => {
    const k = (title || '').toLowerCase();
    if (!k) return;
    const b = bucket.get(k) || { title, count: 0, intensities: [], receipts: [] };
    b.count += 1;
    if (Number.isFinite(intensity)) b.intensities.push(intensity);
    if (receipts?.length) b.receipts = b.receipts.concat(receipts);
    bucket.set(k, b);
  };

  for (const { job } of pairs) {
    const insights = job.insights || [];
    for (const it of insights) {
      const title = it.insight_category || it.title || it.theme || it.topic || '';
      if (!title) continue;
      const overall = (it.insight_sentiment_overall || it.type || it.variant || '').toLowerCase();
      const intensity = toNumber(it.insight_sentiment_score ?? it.sentiment ?? it.intensity);
      const recs = extractReceipts(it, 2);
      if (overall.includes('positive')) feed(pos, capSentence(title), intensity, recs);
      else if (overall.includes('negative') || overall.includes('wish')) feed(neg, capSentence(title), intensity, recs);
    }
  }

  const toTiles = (bucket, variant) => Array.from(bucket.values())
    .map(b => {
      const avgIntensity = avg(b.intensities);
      const score = Math.round(clamp(avgIntensity, 0, 10) * b.count);
      return {
        type: 'insight',
        variant,
        title: b.title,
        count: b.count,
        avgSentiment: Number(avgIntensity.toFixed(1)),
        score,
        receipts: take(b.receipts, 2)
      };
    })
    .sort((a,b) => b.score - a.score)
    .slice(0,4);

  return { positives: toTiles(pos, 'positive'), negatives: toTiles(neg, 'negative') };
}

// ------------ NEXT JOBS (unchanged) ------------
function buildNextJobs(nextGoalsSummary) {
  if (!nextGoalsSummary) return [];
  const items = Array.isArray(nextGoalsSummary.goals) ? nextGoalsSummary.goals : (nextGoalsSummary.items || []);

  const priority = (freq) => {
    const f = String(freq || '').toLowerCase();
    if (f.startsWith('high')) return 3;
    if (f.startsWith('medium')) return 2;
    if (f.startsWith('low')) return 1;
    return 0;
  };

  return [...items]
    .sort((a, b) => priority(b.frequency || b.frequency_estimate) - priority(a.frequency || a.frequency_estimate))
    .slice(0, 12)
    .map((g) => ({
      type: 'nextJob',
      title: g.goal || g.title || '',
      frequency: g.frequency || g.frequency_estimate || '—',
      receipts: (Array.isArray(g.receipts) ? g.receipts : (g.quotes || g.examples || []))
        .slice(0, 2)
        .map(x => ({ quote: (x?.quote || x?.text || x || '').toString(), url: '' }))
    }));
}


// ------------------------ MAIN EXPORT ------------------------
export function getJobSections({ jobName, customerStories = [], jobMaps = {}, jtbdSatisfaction = {}, jtbdNextGoals = {} }) {
  const key = jtbdKeyFromName(jobName);
  const pairs = selectPairsForJob(customerStories, jobName);
  const sectionTotal = pairs.length;

  const relatedChips  = buildRelatedJobsChips(pairs);
  const summaryTile   = buildSummaryOfJob(pairs, jtbdSatisfaction[key] || null);
  const measures      = buildMeasures(pairs);
  const jobMapBoard   = buildJobMap(jobMaps, jobName);
  const triggers      = buildTriggersForJob(pairs);
  const topSituations = buildTopSituationsForJob(pairs, sectionTotal);
  const topDesired = buildTopDesiredOutcomesForJob(pairs, sectionTotal);
//  const situationChips = buildTopSituationsChips(pairs);
//  const outcomeChips   = buildTopDesiredOutcomesChips(pairs);
  const { positives, negatives } = buildGoodBadInsightsForJob(pairs);
  const productTiles  = buildProductsUsedForJob(pairs);
  const nextJobsTiles = buildNextJobs(jtbdNextGoals[key] || null);

  const sections = [
    {
      key: 'job.relatedJobs',
      title: 'Related Jobs', // rename freely
      section_total_interviews: sectionTotal,
      chipsLabel: 'Related Jobs',
      chips: relatedChips
    },
    {
      key: 'job.overview',
      title: 'Summary of Job', // rename freely
      section_total_interviews: sectionTotal,
      tiles: [summaryTile]
    },
    {
      key: 'job.measures',
      title: 'How do customers measure success?', // rename freely
      section_total_interviews: sectionTotal,
      tiles: measures
    },
    {
      key: 'job.jobMap',
      title: 'The Job Map', // rename freely
      type: 'jobMapBoard',
      section_total_interviews: sectionTotal,
      columns: jobMapBoard.columns
    },
    {
      key: 'job.triggers',
      title: 'Why do customers seek out better solutions to this Job? (Triggers)', // rename freely
      section_total_interviews: sectionTotal,
      tiles: triggers
    },
    {
      key: 'job.situations',
      title: 'What are the top situations for doing this Job?', // rename freely
      section_total_interviews: sectionTotal,
      tiles: topSituations
    },
    {
      key: 'job.desiredOutcomes',
      title: 'What are the top desired outcomes for doing this Job?', // rename freely
      section_total_interviews: sectionTotal,
      tiles: topDesired
    },
    {
      key: 'job.goodBad',
      title: "What 'good' & 'bad' look like", // rename freely
      section_total_interviews: sectionTotal,
      subsections: [
        { title: 'Top positive insights', tiles: positives },
        { title: 'Top negative insights (Opportunity Score)', tiles: negatives }
      ]
    },
    {
      key: 'job.importanceSatisfaction',
      title: 'How well Products do this Job (Importance × Satisfaction)', // rename freely
      section_total_interviews: sectionTotal,
      tiles: productTiles
    },
    {
      key: 'job.nextJobs',
      title: 'Next Jobs', // rename freely
      section_total_interviews: sectionTotal,
      tiles: nextJobsTiles
    }
  ];

  return { sections };
}
